import { defineStore } from "pinia";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { scoreSheetFonts } from "@/config/fonts";

export const useScoresheet = defineStore("scoreSheetStore", {
    state: () => ({
        isGenerating: false,

        // ── School Info ──
        school: {
            arabicName:  "كلية ابن مسعود لتحفيظ القرآن والدراسات الإسلامية بباوتشي",
            englishName: "BN MAS'UD COLLEGE FOR TAHFIZ AND ISLAMIC STUDIES",
            address:     "Saleh Manga Primary School, Gwallaga Bauchi, Bauchi State",
            contact:     "bnmasudcollege@gmail.com | 08032981527 | 08036928527 | 08032607393",
        },

        // ── Logo ──
        logo: {
            size:           20,
            portraitMargin: 8,
            landscapeMargin: 12,
        },

        // ── Header Typography ──
        header: {
            arabicFontSize:   16,
            englishFontSize:  16,
            addressFontSize:  13,
            contactFontSize:  10,
            titleFontSize:    14,
            titleBoxWidth:    180,
            titleBoxHeight:   8,
            titleBoxRadius:   1.5,
            dividerStart:     80,  // mm from left
        },

        // ── Colors ──
        colors: {
            arabic:      [0,   0,   150],
            english:     [0,   100, 0  ],
            address:     [60,  60,  60 ],
            contact:     [80,  80,  80 ],
            title:       [0,   0,   0  ],
            titleBg:     [240, 248, 255],
            divider:     [200, 200, 200],
            tableText:   [0,   0,   0  ],
            tableLine:   [0,   0,   0  ],
            tableHeadBg: [255, 255, 255],
        },

        // ── Table Font Sizes ──
        // [simple, standard, compact]
        fontSize: {
            head: [10, 8, 5],
            subj: [10, 9, 6],
            assm: [10, 7, 5],
            body: [10, 10, 7],
        },

        // ── Column Widths ──
        // [simple, standard, compact]
        colWidth: {
            sn:    [12, 8,  6 ],
            total: [16, 12, 8 ],
            avg:   [16, 14, 12],
            pos:   [16, 14, 12],
        },

        // ── Name Column ──
        nameCol: {
            simple:   { min: 50, max: 80, factor: 2.5 },
            standard: { min: 40, max: 70, factor: 2.2 },
            compact:  { min: 30, max: 50, factor: 2.0 },
        },

        // ── Subject Col Base Widths (before scaling) ──
        subjectCol: {
            standard: { min: 15, max: 30, base: 15, factor: 1.8 },
            compact:  { min: 6,  max: 15, base: 6,  factor: 1.2 },
        },

        // ── Simple Mode Min Assessment Col Width ──
        simpleAssessmentColMinW: 14,

        // ── Page Margins ──
        // [simple, standard, compact]
        pageMargin: [10, 5, 3],

        // ── Cell Padding ──
        cellPadding: {
            simple:   2,
            standard: { top: 1.2, right: 0.8,  bottom: 1.2, left: 0.8  },
            compact:  { top: 0.6, right: 0.25, bottom: 0.6, left: 0.25 },
        },

        // ── Table Line Width ──
        lineWidth: 0.1,
    }),

    actions: {
        async generateScoreSheet({ classesData, assessments, termName, sessionYear, logo }) {
            this.isGenerating = true;

            try {
                const getOrientation = (cls) =>
                    (cls.selectedSubjects?.length ?? 0) <= 3 ? "portrait" : "landscape";

                const firstValid = classesData.find(c => (c.students?.length ?? 0) > 0);
                if (!firstValid) throw new Error("No classes with students found.");

                const doc = new jsPDF({ orientation: getOrientation(firstValid) });

                if (scoreSheetFonts?.length) {
                    await Promise.all(
                        scoreSheetFonts.map(async (f) => {
                            const res = await fetch(f.url);
                            const buf = await res.arrayBuffer();
                            let binary = "";
                            new Uint8Array(buf).forEach((b) => (binary += String.fromCharCode(b)));
                            doc.addFileToVFS(f.fileName, btoa(binary));
                            doc.addFont(f.fileName, f.fontName, f.weight);
                        })
                    );
                }

                const logoBase64 = await this.convertImgToBase64(logo);
                let firstPageDrawn = false;

                for (let c = 0; c < classesData.length; c++) {
                    const cls      = classesData[c];
                    const subjects = cls.selectedSubjects || [];
                    const students = (cls.students || []).slice().sort((a, b) =>
                        `${a.first_name} ${a.last_name}`.toLowerCase()
                            .localeCompare(`${b.first_name} ${b.last_name}`.toLowerCase())
                    );

                    if (!students.length) continue;

                    const orientation = getOrientation(cls);
                    const isSimple    = subjects.length <= 3;
                    const isCompact   = subjects.length > 8;
                    const modeIdx     = isSimple ? 0 : (isCompact ? 2 : 1);

                    if (!firstPageDrawn) {
                        firstPageDrawn = true;
                    } else {
                        doc.addPage(undefined, orientation);
                    }

                    const pageWidth  = doc.internal.pageSize.getWidth();
                    const pageMargin = this.pageMargin[modeIdx];
                    let y = 10;

                    // ── Header ──
                    const logoSize   = this.logo.size;
                    const logoMargin = orientation === "portrait"
                        ? this.logo.portraitMargin
                        : this.logo.landscapeMargin;

                    if (logoBase64) {
                        doc.addImage(logoBase64, "JPEG", logoMargin, y + 2, logoSize, logoSize);
                        doc.addImage(logoBase64, "JPEG", pageWidth - logoMargin - logoSize, y + 2, logoSize, logoSize);
                    }

                    y += orientation === "portrait" ? 6 : 4;

                    doc.setFont("AeCortoba", "normal");
                    doc.setFontSize(this.header.arabicFontSize);
                    doc.setTextColor(...this.colors.arabic);
                    doc.text(this.school.arabicName, pageWidth / 2, y + 5, { align: "center" });

                    y += 10;
                    doc.setFont("Lateef-Bold", "bold");
                    doc.setFontSize(this.header.englishFontSize);
                    doc.setTextColor(...this.colors.english);
                    doc.text(this.school.englishName, pageWidth / 2, y, { align: "center" });

                    y += 7;
                    doc.setDrawColor(...this.colors.divider);
                    doc.setLineWidth(0.3);
                    doc.line(this.header.dividerStart, y, pageWidth - this.header.dividerStart, y);

                    y += 6;
                    doc.setFont("Lateef", "normal");
                    doc.setFontSize(this.header.addressFontSize);
                    doc.setTextColor(...this.colors.address);
                    doc.text(this.school.address, pageWidth / 2, y, { align: "center" });

                    y += 6;
                    doc.setFontSize(this.header.contactFontSize);
                    doc.setTextColor(...this.colors.contact);
                    doc.text(this.school.contact, pageWidth / 2, y, { align: "center" });

                    y += 8;
                    doc.setFillColor(...this.colors.titleBg);
                    doc.roundedRect(
                        (pageWidth - this.header.titleBoxWidth) / 2,
                        y - 5,
                        this.header.titleBoxWidth,
                        this.header.titleBoxHeight,
                        this.header.titleBoxRadius,
                        this.header.titleBoxRadius,
                        "F"
                    );
                    doc.setFont("Lateef-Bold", "bold");
                    doc.setFontSize(this.header.titleFontSize);
                    doc.setTextColor(...this.colors.title);
                    doc.text(
                        `${cls.name} ${termName} ${sessionYear} SCORE SHEET`.toUpperCase(),
                        pageWidth / 2, y, { align: "center" }
                    );

                    y += 10;

                    // ── Assessment lookup ──
                    const getSubjectAssessments = (subjectId) =>
                        assessments.filter(a =>
                            a.subjects?.some(s =>
                                String(s.id) === String(subjectId) &&
                                String(s.pivot?.class_room_id) === String(cls.id)
                            )
                        );

                    // ── Font sizes ──
                    const fsHead = this.fontSize.head[modeIdx];
                    const fsSubj = this.fontSize.subj[modeIdx];
                    const fsAssm = this.fontSize.assm[modeIdx];
                    const fsBody = this.fontSize.body[modeIdx];

                    // ── Fixed column widths ──
                    const SN_W    = this.colWidth.sn[modeIdx];
                    const TOTAL_W = this.colWidth.total[modeIdx];
                    const AVG_W   = this.colWidth.avg[modeIdx];
                    const POS_W   = this.colWidth.pos[modeIdx];

                    const maxNameLength = Math.max(
                        ...students.map(s => `${s.first_name} ${s.last_name} | ${s.id ?? "-"}`.length),
                        20
                    );

                    const nameCfg = isSimple
                        ? this.nameCol.simple
                        : isCompact
                            ? this.nameCol.compact
                            : this.nameCol.standard;

                    const NAME_W = Math.max(nameCfg.min, Math.min(nameCfg.max, maxNameLength * nameCfg.factor));

                    const fixedWidth           = SN_W + NAME_W + TOTAL_W + AVG_W + POS_W;
                    const usableWidth          = pageWidth - 2 * pageMargin;
                    const availableForSubjects = usableWidth - fixedWidth;

                    const totalAssessmentCols = subjects.reduce((sum, s) =>
                        sum + (getSubjectAssessments(s.id).length || 1), 0
                    );

                    // ── STANDARD / COMPACT MODE ──
                    if (!isSimple) {
                        const subjCfg = isCompact
                            ? this.subjectCol.compact
                            : this.subjectCol.standard;

                        const subjectColWidths = subjects.map(s => {
                            const aCount     = getSubjectAssessments(s.id).length || 1;
                            const textLength = (s.arabic || s.name || "").trim().length;
                            const baseW      = Math.max(subjCfg.min, Math.min(subjCfg.max, subjCfg.base + textLength * subjCfg.factor));
                            return { aCount, baseW };
                        });

                        const totalBaseW  = subjectColWidths.reduce((sum, s) => sum + s.baseW, 0);
                        const scaleFactor = totalBaseW > availableForSubjects
                            ? availableForSubjects / totalBaseW
                            : 1;

                        const finalWidths = subjectColWidths.map(s => ({
                            aCount: s.aCount,
                            perCol: Math.max(isCompact ? 5 : 10, (s.baseW * scaleFactor) / s.aCount),
                        }));

                        const columnStyles = {
                            0: { cellWidth: SN_W,   fontSize: fsHead, halign: "center" },
                            1: { cellWidth: NAME_W, fontSize: fsHead, halign: "left", overflow: "linebreak" },
                        };

                        let col = 2;
                        finalWidths.forEach(({ aCount, perCol }) => {
                            for (let i = 0; i < aCount; i++) {
                                columnStyles[col++] = { cellWidth: perCol };
                            }
                        });
                        columnStyles[col++] = { cellWidth: TOTAL_W };
                        columnStyles[col++] = { cellWidth: AVG_W };
                        columnStyles[col]   = { cellWidth: POS_W };

                        const tableWidth = fixedWidth +
                            finalWidths.reduce((s, { aCount, perCol }) => s + aCount * perCol, 0);
                        const leftMargin = Math.max(pageMargin, (pageWidth - tableWidth) / 2);

                        const headRow1 = [
                            { content: "S/N",                 rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                            { content: "Name | Admission No", rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                            ...subjects.map(s => ({
                                content: s.arabic || s.name,
                                colSpan: getSubjectAssessments(s.id).length || 1,
                                styles:  { font: "Amiri-Bold", fontSize: fsSubj, halign: "center", valign: "middle" },
                            })),
                            { content: "Total",    rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                            { content: "Average",  rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                            { content: "Position", rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                        ];

                        const headRow2 = subjects.flatMap(s => {
                            const sa = getSubjectAssessments(s.id);
                            return sa.length
                                ? sa.map(a => ({ content: a.type?.name ?? a.name, styles: { fontSize: fsAssm, halign: "center" } }))
                                : [{ content: "Score", styles: { fontSize: fsAssm, halign: "center" } }];
                        });

                        const bodyRows = students.map((stu, i) => [
                            i + 1,
                            `${stu.first_name} ${stu.last_name} | ${stu.id ?? "-"}`,
                            ...subjects.flatMap(s => Array(getSubjectAssessments(s.id).length || 1).fill("")),
                            "", "", ""
                        ]);

                        autoTable(doc, {
                            startY: y,
                            margin: { left: leftMargin },
                            head: [headRow1, headRow2],
                            body: bodyRows,
                            theme: "grid",
                            styles: {
                                font: "Amiri",
                                fontSize: fsBody,
                                cellPadding: this.cellPadding[isCompact ? "compact" : "standard"],
                                halign: "center",
                                valign: "middle",
                                lineColor: this.colors.tableLine,
                                lineWidth: this.lineWidth,
                                textColor: this.colors.tableText,
                            },
                            headStyles: {
                                font: "Lateef-Bold",
                                fillColor: this.colors.tableHeadBg,
                                textColor: this.colors.tableText,
                                lineColor: this.colors.tableLine,
                                lineWidth: this.lineWidth,
                            },
                            columnStyles,
                            showHead: "firstPage",
                        });

                        continue;
                    }

                    // ── SIMPLE MODE (subjects <= 3) ──
                    const assessmentColW = Math.max(
                        this.simpleAssessmentColMinW,
                        availableForSubjects / totalAssessmentCols
                    );

                    const columnStyles = {
                        0: { cellWidth: SN_W,   fontSize: fsHead, halign: "center" },
                        1: { cellWidth: NAME_W, fontSize: fsHead, halign: "left"   },
                    };

                    let col = 2;
                    subjects.forEach(s => {
                        const aCount = getSubjectAssessments(s.id).length || 1;
                        for (let i = 0; i < aCount; i++) {
                            columnStyles[col++] = { cellWidth: assessmentColW };
                        }
                    });
                    columnStyles[col++] = { cellWidth: TOTAL_W };
                    columnStyles[col++] = { cellWidth: AVG_W };
                    columnStyles[col]   = { cellWidth: POS_W };

                    const headRow1 = [
                        { content: "S/N",                 rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                        { content: "Name | Admission No", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                        ...subjects.map(s => ({
                            content: s.arabic || s.name,
                            colSpan: getSubjectAssessments(s.id).length || 1,
                            styles:  { font: "Amiri-Bold", halign: "center", valign: "middle" },
                        })),
                        { content: "Total",    rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                        { content: "Average",  rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                        { content: "Position", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                    ];

                    const headRow2 = subjects.flatMap(s => {
                        const sa = getSubjectAssessments(s.id);
                        return sa.length
                            ? sa.map(a => ({ content: a.type?.name ?? a.name, styles: { halign: "center" } }))
                            : [{ content: "Score", styles: { halign: "center" } }];
                    });

                    const bodyRows = students.map((stu, i) => [
                        i + 1,
                        `${stu.first_name} ${stu.last_name} | ${stu.id ?? "-"}`,
                        ...subjects.flatMap(s => Array(getSubjectAssessments(s.id).length || 1).fill("")),
                        "", "", ""
                    ]);

                    autoTable(doc, {
                        startY: y,
                        margin: { left: pageMargin, right: pageMargin },
                        head: [headRow1, headRow2],
                        body: bodyRows,
                        theme: "grid",
                        tableWidth: "auto",
                        styles: {
                            font: "Lateef",
                            fontSize: fsBody,
                            cellPadding: this.cellPadding.simple,
                            halign: "center",
                            valign: "middle",
                            lineColor: this.colors.tableLine,
                            lineWidth: this.lineWidth,
                            textColor: this.colors.tableText,
                        },
                        headStyles: {
                            fillColor: this.colors.tableHeadBg,
                            textColor: this.colors.tableText,
                            font: "Lateef-Bold",
                            fontStyle: "bold",
                            lineColor: this.colors.tableLine,
                            lineWidth: this.lineWidth,
                        },
                        columnStyles,
                        showHead: "firstPage",
                    });
                }

                const fileName = classesData.length === 1
                    ? `${classesData[0].name}_${termName}_${sessionYear}_Score_Sheet`.replace(/\s+/g, "_").toUpperCase()
                    : `${termName}_${sessionYear}_Score_Sheets`.replace(/\s+/g, "_").toUpperCase();

                doc.save(`${fileName}.pdf`);

            } finally {
                this.isGenerating = false;
            }
        },

        async convertImgToBase64(img) {
            if (!img) return null;
            const res = await fetch(img);
            const buf = await res.arrayBuffer();
            let binary = "";
            new Uint8Array(buf).forEach((b) => (binary += String.fromCharCode(b)));
            return `data:image/jpeg;base64,${btoa(binary)}`;
        },
    },
});